import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Save, RefreshCw, Settings, Globe, Home, Users, ShoppingBag, Mail, 
  Award, TrendingUp, Heart, Info, Search, Filter, Eye, Edit3,
  ChevronDown, ChevronRight, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { settingsAPI } from '../../services/settingsAPI';

const AdminSettingsPage = () => {
  const { token } = useAuth();
  const { t, language } = useLanguage();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('homepage');
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [editedSettings, setEditedSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [savingKey, setSavingKey] = useState(null);
  const [savedKeys, setSavedKeys] = useState(new Set());

  // Configuration des catégories et sections
  const categoryConfig = {
    homepage: {
      icon: <Home className="h-5 w-5" />,
      name: 'Page d\'Accueil',
      color: 'from-blue-500 to-purple-600',
      sections: ['hero', 'categories', 'featured_products', 'about', 'stats', 'why_choose_tolou', 'newsletter']
    },
    header: {
      icon: <Globe className="h-5 w-5" />,
      name: 'En-tête & Navigation',
      color: 'from-green-500 to-teal-600',
      sections: ['branding', 'navigation']
    },
    footer: {
      icon: <Info className="h-5 w-5" />,
      name: 'Pied de Page',
      color: 'from-orange-500 to-red-600',
      sections: ['company', 'contact', 'social']
    },
    auth: {
      icon: <Users className="h-5 w-5" />,
      name: 'Authentification',
      color: 'from-purple-500 to-pink-600',
      sections: ['login', 'register']
    },
    products: {
      icon: <ShoppingBag className="h-5 w-5" />,
      name: 'Pages Produits',
      color: 'from-indigo-500 to-blue-600',
      sections: ['page', 'filters', 'sorting', 'results']
    },
    categories: {
      icon: <Award className="h-5 w-5" />,
      name: 'Pages Catégories',
      color: 'from-yellow-500 to-orange-600',
      sections: ['page', 'actions']
    },
    cart: {
      icon: <ShoppingBag className="h-5 w-5" />,
      name: 'Panier & Commandes',
      color: 'from-red-500 to-pink-600',
      sections: ['page', 'totals', 'actions']
    },
    profile: {
      icon: <Users className="h-5 w-5" />,
      name: 'Profil & Commandes',
      color: 'from-teal-500 to-green-600',
      sections: ['page', 'sections', 'actions']
    },
    contact: {
      icon: <Mail className="h-5 w-5" />,
      name: 'Page Contact',
      color: 'from-blue-500 to-indigo-600',
      sections: ['page', 'form']
    },
    about: {
      icon: <Info className="h-5 w-5" />,
      name: 'Page À Propos',
      color: 'from-gray-500 to-slate-600',
      sections: ['page']
    }
  };

  // Icônes pour les sections
  const sectionIcons = {
    hero: <Heart className="h-4 w-4" />,
    categories: <Award className="h-4 w-4" />,
    featured_products: <Award className="h-4 w-4" />,
    about: <Info className="h-4 w-4" />,
    stats: <TrendingUp className="h-4 w-4" />,
    why_choose_tolou: <Heart className="h-4 w-4" />,
    newsletter: <Mail className="h-4 w-4" />,
    branding: <Globe className="h-4 w-4" />,
    navigation: <Globe className="h-4 w-4" />,
    company: <Info className="h-4 w-4" />,
    contact: <Mail className="h-4 w-4" />,
    social: <Globe className="h-4 w-4" />,
    login: <Users className="h-4 w-4" />,
    register: <Users className="h-4 w-4" />,
    page: <Eye className="h-4 w-4" />,
    filters: <Filter className="h-4 w-4" />,
    sorting: <TrendingUp className="h-4 w-4" />,
    results: <Search className="h-4 w-4" />,
    actions: <Edit3 className="h-4 w-4" />,
    totals: <TrendingUp className="h-4 w-4" />,
    sections: <Info className="h-4 w-4" />,
    form: <Edit3 className="h-4 w-4" />
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await settingsAPI.getAll(token);
        setSettings(data.settings);
      // Initialiser editedSettings avec les valeurs actuelles
        const initialEdited = {};
      data.settings.forEach(s => {
        initialEdited[s.setting_key] = s.setting_value;
        });
        setEditedSettings(initialEdited);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setEditedSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      // Vérifier s'il y a des changements
      const changesExist = settings.some(s => newSettings[s.setting_key] !== s.setting_value);
      setHasChanges(changesExist);
      return newSettings;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const changesToSave = settings.filter(s => editedSettings[s.setting_key] !== s.setting_value)
                                    .map(s => ({
                                      setting_key: s.setting_key,
                                      setting_value: editedSettings[s.setting_key]
                                    }));

      if (changesToSave.length > 0) {
        await settingsAPI.updateBatch(changesToSave, token);
        // Recharger les paramètres
        fetchSettings();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOne = async (key) => {
    try {
      setSavingKey(key);
      await settingsAPI.update(key, editedSettings[key], token);
      // refléter la sauvegarde côté local
      setSettings(prev => prev.map(s => s.setting_key === key ? { ...s, setting_value: editedSettings[key] } : s));
      setSavedKeys(prev => new Set(prev).add(key));
      // recalcul hasChanges
      const changesExist = settings.some(s => (s.setting_key === key ? editedSettings[key] : editedSettings[s.setting_key]) !== (s.setting_key === key ? editedSettings[key] : s.setting_value));
      setHasChanges(changesExist);
    } catch (e) {
      console.error('Save one failed', e);
    } finally {
      setSavingKey(null);
      setTimeout(() => {
        setSavedKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
      }, 1500);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Grouper les paramètres par catégorie et section
  const groupedSettings = useMemo(() => {
    const groups = {};
    
    settings.forEach(setting => {
      if (!groups[setting.category]) {
        groups[setting.category] = {};
      }
      if (!groups[setting.category][setting.section]) {
        groups[setting.category][setting.section] = [];
      }
      groups[setting.category][setting.section].push(setting);
    });

    // Trier les paramètres par sort_order dans chaque section
    Object.keys(groups).forEach(category => {
      Object.keys(groups[category]).forEach(section => {
        groups[category][section].sort((a, b) => a.sort_order - b.sort_order);
      });
    });

    return groups;
  }, [settings]);

  // Filtrer les paramètres selon la recherche et la catégorie
  const filteredSettings = useMemo(() => {
    let filtered = settings;
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(s => s.category === filterCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(s => 
        s.display_name_fr.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.display_name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.description_fr.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.description_en.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  }, [settings, filterCategory, searchTerm]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="flex items-center space-x-3 text-yellow-500">
          <RefreshCw className="animate-spin h-8 w-8" />
          <span className="text-lg">Chargement des paramètres...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 xl:px-8 2xl:px-10 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-yellow-600 rounded-xl">
              <Settings className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">Paramètres du Site</h1>
              <p className="text-gray-400">Gérez tous les textes et liens de votre site</p>
            </div>
          </div>
          
          {/* Barre d'actions */}
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Recherche */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un paramètre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                />
        </div>

              {/* Filtre par catégorie */}
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white"
              >
                <option value="all">Toutes les catégories</option>
                {Object.keys(categoryConfig).map(category => (
                  <option key={category} value={category}>
                    {categoryConfig[category].name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex gap-3">
          <button
            onClick={fetchSettings}
                disabled={saving}
                className="flex items-center px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
                <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="flex items-center px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>

        {/* Indicateur de changements */}
        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-yellow-600/20 border border-yellow-500/50 rounded-lg flex items-center space-x-3"
          >
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <span className="text-yellow-200">
              Vous avez des modifications non enregistrées. N'oubliez pas de sauvegarder !
            </span>
          </motion.div>
        )}

        {/* Navigation par catégories */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {Object.entries(categoryConfig).map(([categoryKey, config]) => (
            <button
              key={categoryKey}
              onClick={() => setActiveCategory(categoryKey)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                activeCategory === categoryKey
                  ? 'border-yellow-500 bg-gradient-to-br ' + config.color
                  : 'border-gray-700 bg-gray-800 hover:border-gray-600'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <div className={`p-2 rounded-lg ${
                  activeCategory === categoryKey ? 'bg-white/20' : 'bg-gray-700'
                }`}>
                  {config.icon}
                </div>
                <span className={`text-sm font-medium text-center ${
                  activeCategory === categoryKey ? 'text-white' : 'text-gray-300'
                }`}>
                  {config.name}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Contenu des paramètres */}
        <div className="space-y-6">
          {categoryConfig[activeCategory]?.sections.map(section => {
            const sectionSettings = groupedSettings[activeCategory]?.[section] || [];
            if (sectionSettings.length === 0) return null;
            
            const isExpanded = expandedSections.has(section);
                
                return (
                  <motion.div
                key={section}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden"
              >
                {/* En-tête de section */}
                <button
                  onClick={() => toggleSection(section)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-700 rounded-lg">
                      {sectionIcons[section]}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-white capitalize">
                        {section === 'why_choose_tolou' ? 'Pourquoi Choisir TOLOU' : 
                         section === 'featured_products' ? 'Produits Vedettes' :
                         section.replace('_', ' ')}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {sectionSettings.length} paramètre{sectionSettings.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {/* Contenu de la section */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-gray-700"
                    >
                      <div className="p-4 space-y-4">
                        {sectionSettings.map(setting => {
                          const isChanged = editedSettings[setting.setting_key] !== setting.setting_value;
                          const isSaving = savingKey === setting.setting_key;
                          const isSaved = savedKeys.has(setting.setting_key);
                          
                          return (
                            <div key={setting.setting_key} className="bg-gray-700 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <label className="block text-sm font-medium text-gray-200 mb-1">
                                    {language === 'fr' ? setting.display_name_fr : setting.display_name_en}
                                    {isChanged && (
                                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                                        <Edit3 className="h-3 w-3 mr-1" />
                                        Modifié
                                      </span>
                                    )}
                                    {isSaving && (
                                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                        Sauvegarde...
                                      </span>
                                    )}
                                    {isSaved && (
                                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Sauvegardé
                                      </span>
                                    )}
                                  </label>
                                  {setting.description_fr && (
                                    <p className="text-xs text-gray-400 mb-2">
                                      {language === 'fr' ? setting.description_fr : setting.description_en}
                                    </p>
                                  )}
                                  <div className="text-xs text-gray-500 font-mono bg-gray-800 px-2 py-1 rounded">
                                    {setting.setting_key}
                                  </div>
                        </div>
                      </div>
                      
                              <div className="space-y-3">
                                {/* Version française */}
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">
                                    Français
                        </label>
                                  <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editedSettings[setting.setting_key] || ''}
                                    onChange={(e) => handleSettingChange(setting.setting_key, e.target.value)}
                                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                                    placeholder={`Valeur en français`}
                                  />
                                  <button
                                    onClick={() => handleSaveOne(setting.setting_key)}
                                    disabled={savingKey === setting.setting_key}
                                    className="shrink-0 inline-flex items-center justify-center gap-1.5 w-28 px-3 py-2 bg-gradient-to-r from-yellow-600 to-yellow-500 text-gray-900 font-medium rounded-md hover:from-yellow-500 hover:to-yellow-400 disabled:opacity-50 shadow-sm border border-yellow-500/40"
                                    title="Enregistrer ce champ"
                                  >
                                    {savingKey === setting.setting_key ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Save className="h-4 w-4" />
                                    )}
                                    <span>Sauver</span>
                                  </button>
                                  {savedKeys.has(setting.setting_key) && (
                                    <span className="text-green-400 text-xs">Enregistré</span>
                                  )}
                                </div>
                                </div>

                                {/* Version anglaise (si elle existe) */}
                                {setting.display_name_en && (
                                  <div>
                                    <label className="block text-xs text-gray-400 mb-1">
                                      English
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={editedSettings[`${setting.setting_key.replace('_fr', '_en')}`] || ''}
                                        onChange={(e) => {
                                          const enKey = setting.setting_key.replace('_fr', '_en');
                                          handleSettingChange(enKey, e.target.value);
                                        }}
                                        className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                                        placeholder={`Value in English`}
                                      />
                                      <button
                                        onClick={() => handleSaveOne(setting.setting_key.replace('_fr', '_en'))}
                                        disabled={savingKey === setting.setting_key.replace('_fr', '_en')}
                                        className="shrink-0 inline-flex items-center justify-center gap-1.5 w-28 px-3 py-2 bg-gradient-to-r from-yellow-600 to-yellow-500 text-gray-900 font-medium rounded-md hover:from-yellow-500 hover:to-yellow-400 disabled:opacity-50 shadow-sm border border-yellow-500/40"
                                        title="Save this field"
                                      >
                                        {savingKey === setting.setting_key.replace('_fr', '_en') ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <Save className="h-4 w-4" />
                                        )}
                                        <span>Sauver</span>
                                      </button>
                                      {savedKeys.has(setting.setting_key.replace('_fr', '_en')) && (
                                        <span className="text-green-400 text-xs">Saved</span>
                                      )}
                                    </div>
                                  </div>
                        )}
                      </div>
                    </div>
                );
              })}
            </div>
          </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
        </div>
      </div>
    );
  };
  
  export default AdminSettingsPage;
